export type TimeFormat = "standard" | "timecode" | "frames";

const DEFAULT_FPS = 30;

function safeFps(fps: number): number {
  return Math.max(1, Math.round(fps || DEFAULT_FPS));
}

function pad(num: number, len = 2): string {
  return Math.floor(num).toString().padStart(len, "0");
}

function frameDigits(fps: number): number {
  return safeFps(fps) >= 100 ? 3 : 2;
}

function framesFromTime(
  time: number,
  fps: number,
): { h: number; m: number; s: number; f: number } {
  const fpsSafe = safeFps(fps);
  const totalFrames = Math.round(Math.max(0, time) * fpsSafe);
  const wholeSeconds = Math.floor(totalFrames / fpsSafe);
  const f = totalFrames % fpsSafe;
  const h = Math.floor(wholeSeconds / 3600);
  const m = Math.floor((wholeSeconds % 3600) / 60);
  const s = wholeSeconds % 60;
  return { h, m, s, f };
}

export function formatTimecode(time: number, fps: number): string {
  const { h, m, s, f } = framesFromTime(time, fps);
  return `${pad(h)}:${pad(m)}:${pad(s)}:${pad(f, frameDigits(fps))}`;
}

export function formatStandard(
  time: number,
  fps: number,
  hideFrames?: boolean,
): string {
  const { h, m, s, f } = framesFromTime(time, fps);
  const base = h > 0 ? `${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
  return hideFrames ? base : `${base}:${pad(f, frameDigits(fps))}`;
}

export function formatFrames(time: number, fps: number): string {
  return String(Math.floor(Math.max(0, time) * safeFps(fps)));
}

export function parseTimecodeToSeconds(
  text: string,
  fps: number,
): number | null {
  if (!text) return null;
  const parts = text.trim().split(":").map((p) => p.trim());
  if (parts.length < 2 || parts.length > 4) return null;
  const nums = parts.map((p) => (p === "" ? Number.NaN : Number(p)));
  if (nums.some((n) => Number.isNaN(n))) return null;

  let h = 0;
  let m = 0;
  let s = 0;
  let f = 0;
  if (parts.length === 4) [h, m, s, f] = nums;
  else if (parts.length === 3) [m, s, f] = nums;
  else [s, f] = nums;

  const fpsSafe = safeFps(fps);
  const seconds = ((h * 3600 + m * 60 + s) * fpsSafe + f) / fpsSafe;
  return Number.isFinite(seconds) && seconds >= 0 ? seconds : null;
}
